"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { Drawer } from "@base-ui/react/drawer";
import { Popover } from "@base-ui/react/popover";
import { useEffect, useRef, useState } from "react";
import { useShouldUseDrawer } from "../../hooks/useShouldUseDrawer.js";
import { PopoverModeContext } from "./PopoverContext.js";
function handleBgClick() {
    const cleanup = ()=>{
        document.removeEventListener('click', onClick, true);
        clearTimeout(timer);
    };
    const onClick = (e)=>{
        e.preventDefault();
        e.stopPropagation();
        cleanup();
    };
    const timer = setTimeout(cleanup, 500);
    document.addEventListener('click', onClick, true);
}
const INSIDE_SELECTOR = '[role="dialog"], [role="listbox"], [role="menu"], [data-popup-open], [data-base-ui-focusable]';
function PopoverRoot({ open, defaultOpen, onOpenChange, onOpenChangeComplete, modal, disablePointerDismissal, isDrawerModal = false, snapPoints, mode: modeProp, children }) {
    const shouldUseDrawer = useShouldUseDrawer();
    const mode = modeProp ?? (shouldUseDrawer ? 'drawer' : 'popover');
    const useDrawer = 'drawer' === mode;
    const blocksBackground = true === modal || isDrawerModal;
    const actionsRef = useRef(null);
    const [isOpen, setIsOpen] = useState(open ?? defaultOpen ?? false);
    useEffect(()=>{
        if (void 0 !== open) setIsOpen(open);
    }, [
        open
    ]);
    useEffect(()=>{
        if (!isOpen || !blocksBackground) return;
        const isOutside = (e)=>!(e.target instanceof Element && e.target.closest(INSIDE_SELECTOR));
        const onPress = (e)=>{
            if (isOutside(e)) handleBgClick();
        };
        const guard = (e)=>{
            if (!isOutside(e)) return;
            e.preventDefault();
            e.stopPropagation();
            if (!(useDrawer && disablePointerDismissal)) actionsRef.current?.close();
        };
        document.addEventListener('pointerdown', onPress, true);
        document.addEventListener('click', guard, true);
        return ()=>{
            document.removeEventListener('pointerdown', onPress, true);
            document.removeEventListener('click', guard, true);
        };
    }, [
        isOpen,
        blocksBackground,
        useDrawer,
        disablePointerDismissal
    ]);
    const handleOpenChange = (nextOpen, eventDetails)=>{
        if (void 0 === open) setIsOpen(nextOpen);
        if (!nextOpen && blocksBackground && 'outside-press' === eventDetails.reason && 'click' !== eventDetails.event.type) handleBgClick();
        onOpenChange?.(nextOpen, eventDetails);
    };
    return /*#__PURE__*/ jsx(PopoverModeContext.Provider, {
        value: mode,
        children: useDrawer ? /*#__PURE__*/ jsxs(Drawer.Root, {
            actionsRef: actionsRef,
            open: open,
            defaultOpen: defaultOpen,
            onOpenChange: handleOpenChange,
            onOpenChangeComplete: onOpenChangeComplete,
            modal: isDrawerModal || (modal ?? false),
            disablePointerDismissal: disablePointerDismissal ?? false,
            snapPoints: snapPoints,
            children: [
                isDrawerModal && /*#__PURE__*/ jsx(Drawer.Backdrop, {
                    className: "fixed inset-0 z-[1] min-h-dvh bg-neutral-13/50 transition-opacity duration-150 data-[ending-style]:opacity-0 data-[starting-style]:opacity-0"
                }),
                children
            ]
        }) : /*#__PURE__*/ jsx(Popover.Root, {
            actionsRef: actionsRef,
            open: open,
            defaultOpen: defaultOpen,
            onOpenChange: handleOpenChange,
            onOpenChangeComplete: onOpenChangeComplete,
            modal: modal,
            children: children
        })
    });
}
export { PopoverRoot };
